import React, { Suspense, useCallback } from 'react';
import { EngagementCoordinationClient } from '@helpcenter/engagement-coordination-client';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { isProjectMappingInHelpCenterEnabled, isCustomPagesUiEnabled } from '@helpcenter/feature-flags';
import FeedbackCollectorButton from '@helpcenter/feedback-collector-button';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useLazyLoadQuery } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import { LeftSidebarWithoutResize } from '@atlaskit/page-layout';
import { Box, xcss } from '@atlaskit/primitives';
import {
    GoBackItem,
    ButtonItem,
    HeadingItem,
    Section,
    SideNavigation,
    NavigationHeader,
    NavigationContent,
} from '@atlaskit/side-navigation';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv, getIsHCCustomizationEnabled } from '@atlassian/help-center-common-util/env';
import { getWindow } from '@atlassian/help-center-common-util/get-window';
import { useHelpCenterMessageDictionary } from '../../utils/help-center-messages';
import messages from './messages';
import { SidebarHelpCenterHeader } from './sidebar-help-center-header';
import { SidebarSkeleton } from './sidebar-skeleton';
export interface NavigationContentItem {
    onClick: () => void;
    iconBefore?: React.ReactElement;
    text: string;
    isVisible: boolean;
    isSelected?: boolean;
}

interface SidebarProps {
    width: number;
    navigationContentItems: NavigationContentItem[];
    featureName: string;
}

const Sidebar: React.FC<SidebarProps> = ({ width, navigationContentItems, featureName }) => {
    di(
        isProjectMappingInHelpCenterEnabled,
        getEnv,
        useLazyLoadQuery,
        EngagementCoordinationClient,
        isCustomPagesUiEnabled,
        getIsHCCustomizationEnabled,
        isCSMHelpCenter,
        getWindow,
        useRouterActions
    );
    const { push } = useRouterActions();
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const helpCenterMessages = useHelpCenterMessageDictionary();
    const onBackButtonClick = useCallback(() => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'click',
            actionSubjectId: `SidebarBackButton`,
        }).fire();
        if (!isProjectMappingInHelpCenterEnabled()) {
            getWindow().location.href = `${getEnv().baseUrl}`;
        } else {
            push(helpCenterMessages.backButtonHref) as void;
        }
    }, [createAnalyticsEvent, push, helpCenterMessages.backButtonHref]);

    return (
        <LeftSidebarWithoutResize width={width}>
            <Box xcss={sidebarContainerStyles}>
                <SideNavigation label={formatMessage(messages.sidebarNavigationLabel)}>
                    <Suspense fallback={<SidebarSkeleton />}>
                        <NavigationHeader>
                            <ErrorBoundary>
                                <SidebarHelpCenterHeader />
                            </ErrorBoundary>
                        </NavigationHeader>
                        <Box padding="space.100">
                            <GoBackItem onClick={onBackButtonClick}>
                                {isProjectMappingInHelpCenterEnabled()
                                    ? formatMessage(helpCenterMessages.backButton)
                                    : formatMessage(messages.backButtonWithoutProjectMapping)}
                            </GoBackItem>
                        </Box>
                        <NavigationContent showTopScrollIndicator>
                            <>
                                {isProjectMappingInHelpCenterEnabled() && (
                                    <HeadingItem>{formatMessage(helpCenterMessages.settingsLabel)}</HeadingItem>
                                )}
                            </>
                            <Section isList>
                                {navigationContentItems.map(({ onClick, iconBefore, text, isSelected, isVisible }) => {
                                    const renderButtonItem = () => (
                                        <ButtonItem
                                            onClick={onClick}
                                            iconBefore={iconBefore}
                                            key={text}
                                            isSelected={isSelected}
                                        >
                                            {text}
                                        </ButtonItem>
                                    );

                                    return isVisible && renderButtonItem();
                                })}
                            </Section>
                        </NavigationContent>
                        <Box xcss={navFooter}>
                            <NavigationContent showTopScrollIndicator>
                                <Section>
                                    <FeedbackCollectorButton
                                        showIcon
                                        showTypeField
                                        featureName={featureName}
                                        buttonLabel={messages.feedbackWidget}
                                        analyticsSubjectId="SidebarFeedbackButton"
                                        useSideNavButton
                                    />
                                </Section>
                            </NavigationContent>
                        </Box>
                    </Suspense>
                </SideNavigation>
            </Box>
        </LeftSidebarWithoutResize>
    );
};

const navFooter = xcss({
    position: 'relative',
});

const sidebarContainerStyles = xcss({
    width: '100%',
    height: '100%',
    borderRightWidth: 'border.width',
    borderRightStyle: 'solid',
    borderColor: 'color.border',
    boxSizing: 'inherit',
});

export default withAnalyticsContext({ component: 'SideBar' })(Sidebar);
